/**
 * Marketo Forms.
 * @format
 */

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable no-undef */

import axios from 'axios';
import { FieldProperties } from './form-modals';
import { scriptLoader } from '@services/third-party-services';

const MARKETO_COMPANY_ID = '760-DZO-155';

interface heapFormDataProps {
  title?: string;
  type?: string;
  id?: string;
  region?: string;
  location: string;
  submissionType: string;
  marketoId?: string | number;
  productId?: string | number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
}

/**
 * Track Heap Form.
 */
export const trackHeapForm = (formData: heapFormDataProps): void => {
  if (window.heap && formData) {
    const {
      title,
      type,
      id,
      region,
      location,
      submissionType,
      marketoId,
      productId,
      data,
    } = formData;

    heap.track(submissionType, {
      title: `${title} Form`,
      type: type === 'email' ? 'Email' : 'Marketo',
      id: id,
      region: region || 'main-content',
      location: location,
      marketoId: marketoId || -1,
      productId: productId || -1,
    });

    if (data) {
      const {
        Email: email,
        FirstName: firstName,
        LastName: lastName,
        Phone: phone,
        Country: country,
        City: city,
      } = data;

      heap.identify(email);

      heap.addUserProperties({
        email: email,
        firstName: firstName,
        lastName: lastName,
        phone: phone,
        country: country,
        city: city,
      });
    }
  }
};

/**
 * Setup Hidden Marketo Form.
 * @param {string} formId
 */
const setupHiddenMarketoForm = (formId) => {
  const marketoForm = document.createElement('form');
  marketoForm.id = `mktoForm_${formId}`;
  marketoForm.classList.add('marketo-hidden-form');
  document.body.appendChild(marketoForm);
};

/**
 * Log Submission Attempt.
 *
 * @param {string} formId
 */
const logSubmissionAttempt = (formId) => {
  const formDetails = {};
  const submissionTime = new Date();
  formDetails.formId = formId;
  formDetails.formIdSelector = `mktoForm_${formId}`;
  formDetails.class = 'marketo-hidden-form';
  formDetails.submissionTime = submissionTime.toString();

  if (!window.drupalSettings.varian.formSubmission) {
    window.drupalSettings.varian.formSubmission = [];
  }

  window.drupalSettings.varian.formSubmission.push(formDetails);
};

/**
 * Check for and create string from multi-select fields.
 *
 * @param formFields
 */
export const checkForMultiSelectFields = (
  formFields: Record<string, string | Array<Record<string, boolean>>>
) => {
  if (formFields) {
    /**
     * Get multi select fields.
     */
    const fieldArray = Object.keys(formFields);
    if (fieldArray.length > 0) {
      fieldArray.forEach((field) => {
        /**
         * Get consent.
         */
        if (
          field.toLowerCase().indexOf('consent') > -1 ||
          field === 'Email_Opt_in__c'
        ) {
          if (
            Array.isArray(formFields[field]) &&
            formFields[field].length > 0
          ) {
            const { consent } = formFields[field][0];

            if (consent) {
              formFields[field] = 'yes';
            }
          }
        }

        /**
         * Create string based off multi-select.
         */
        let fieldString = '';
        if (Array.isArray(formFields[field]) && formFields[field].length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const fieldList = formFields[field].map(
            (checkbox: Record<string, boolean>) => {
              const checkboxProperties = Object.entries(checkbox);
              if (checkboxProperties[0][1]) {
                fieldString += `${checkboxProperties[0][0]}, `;
              }
            }
          );
        }

        if (fieldString !== '') {
          fieldString = fieldString.substring(0, fieldString.length - 2);
          formFields[field] = fieldString;
        }
      });
    }
  }
  return formFields;
};

/**
 * Submit Form to Marketo.
 *
 * @param {string} formId
 */
const submitFormToMarketo = (formId, formFields) => {
  if (window.MktoForms2) {
    MktoForms2.loadForm('//app-sj30.marketo.com', MARKETO_COMPANY_ID, formId);
    MktoForms2.whenReady((form) => {
      form.vals(formFields);
      form.onSuccess(() => {
        return false;
      });
      form.submit();
    });
  } else {
    scriptLoader(
      '/themes/custom/varian/webpack/scripts/third-party/marketo-forms-2.min.js',
      'marketo-form-script',
      () => {
        console.log('Marketo form script loaded. Attempting to resend form.');
        submitFormToMarketo(formId, formFields);
      }
    );
  }
  return false;
};

type FormFieldProps = {
  [key: string]: string;
};

/**
 * Submit Marketo.
 *
 * @param {string} formType
 *   Selector id for the form type.
 * @param {FormFieldProps} formFields
 *   Collection for form field data.
 * @param {string} formId
 *   Marketo form id.
 */
export const submitMarketoForm = (
  formType: string,
  formFields: FormFieldProps
) => {
  if (formType === '') {
    return false;
  }

  const form = document.getElementById(formType);
  if (form) {
    setupHiddenMarketoForm(1094);
    logSubmissionAttempt(1094);
    return submitFormToMarketo(1094, formFields);
  }
};

interface FormRequestProperties {
  data: Array<Record<string, string>>;
  formFields?: FieldProperties | null;
  emailTo?: string | null;
  subject?: string | null;
  replyToField?: string | null;
  requestType: string;
  requestUrl: string;
}

/**
 * Submit Email Form.
 *
 * @param formattedData
 */
export const submitFormRequest = (formSubmissionData): void => {
  const {
    data,
    formFields,
    emailTo,
    subject,
    replyToField,
    requestType,
    responseMessage,
    emailTitle,
  }: FormRequestProperties = formSubmissionData;
  let requestData;

  if (requestType === 'email') {
    const emailFields = formFields.map((field: Record<string, string>) => {
      const { fieldTitle, fieldName } = field;

      if (Array.isArray(data[fieldName]) && data[fieldName].length > 0) {
        let multiReturnValues = ``;

        // Check for multi select value (i.e. checkbox) and format results.
        data[fieldName].forEach((multiSelectField) => {
          const fieldEntries = Object.entries(multiSelectField);
          if (fieldEntries[0][1] === true) {
            multiReturnValues += fieldEntries[0][0] + `, `;
          }

          if (
            fieldEntries[0][0].toLowerCase() === 'other' &&
            fieldEntries[0][1] !== ''
          ) {
            multiReturnValues += `Other: ${fieldEntries[0][1]}, `;
          }
        });

        return {
          title: fieldTitle,
          value: multiReturnValues.slice(0, -2),
        };
      }

      if (data[fieldName]) {
        return {
          title: fieldTitle,
          value: data[fieldName],
        };
      }
    });

    if (data['Email_Opt_in__c']?.[0]?.consent) {
      emailFields.push({ title: 'Email_Opt_in__c', value: 'yes' });
    }

    if (data['dataPrivacyConsent']?.[0]?.consent) {
      emailFields.push({ title: 'dataPrivacyConsent', value: 'yes' });
    }

    requestData = {
      mail: {
        to: emailTo,
        subject: subject,
        replyTo: data[replyToField],
      },
      body: emailFields,
      emailTitle: emailTitle,
      response: {
        responseType: responseMessage,
        submissionData: data,
      },
    };
  } else {
    requestData = data;
  }

  axios
    .post('/varian-mailman/send', { data: requestData })
    .then((response) => {
      console.log(data);
      if (response.status === 200) {
        if (responseMessage) {
          return axios.post('/varian-mailman/send-response', {
            data: requestData,
          });
        }
      } else {
        console.error(response);
      }
    })
    .catch((error) => {
      console.error(`Error sending email: ${error}`);
    });
};

/**
 * Get Form Email.
 *
 * @param {boolean} emailRedirect
 *   Check if email redirect is enabled.
 * @param {string} emailTo
 *   Email address to send form data to.
 * @param {string} formFields
 *   Form fields to check for email redirect.
 * @param submissionData
 *   Form field submission data to parse.
 *
 * @returns {string}
 *   Returns email address to send form data to.
 */
export const getFormEmail = (
  emailRedirect: boolean,
  emailTo: string,
  formFields: string[],
  submissionData
): string => {
  if (!emailRedirect) {
    return emailTo;
  }

  let emailRecipient = '';
  if (formFields.length > 0) {
    formFields.forEach((field) => {
      const { list, useFieldRedirect, fieldName } = field;

      if (useFieldRedirect) {
        const selectedOption = submissionData[fieldName];
        const fieldData = list.filter(
          (listItem) => listItem.name === selectedOption
        );
        const { email } = fieldData[0];
        emailRecipient = email.join(', ');
      }
    });
  }
  return emailRecipient;
};

/**
 *  Update country specific fields
 */
export const getCountryFields = (
  dependentField: FieldProperties,
  selectValue: string
) => {
  if (dependentField.fieldName === 'State') {
    let fieldType;
    switch (selectValue) {
      case 'Canada':
        fieldType = {
          fieldTitle: 'Province',
          placeholderText: 'Select a province',
          useNameAsLookupValue: true,
          errorMessage: 'Province field required.',
        };
        break;
      case 'China':
        fieldType = {
          fieldTitle: 'Province',
          placeholderText: 'Select a province',
          useNameAsLookupValue: false,
          errorMessage: 'Province field required.',
        };
        break;
      case 'Japan':
        fieldType = {
          fieldTitle: 'Prefecture',
          placeholderText: 'Select a prefecture',
          useNameAsLookupValue: false,
          errorMessage: 'Prefecture field required.',
        };
        break;
      case 'France':
        fieldType = {
          fieldTitle: 'Region',
          placeholderText: 'Select a region',
          useNameAsLookupValue: true,
          errorMessage: 'Region field required.',
        };
        break;
      default:
        fieldType = {
          fieldTitle: 'State',
          placeholderText: 'Select a state',
          useNameAsLookupValue: true,
          errorMessage: 'State field required.',
        };
        break;
    }
    return Object.assign(dependentField, fieldType);
  }
  return dependentField;
};
